import { useCallback, useRef } from "react";
import type { TimelineClip, TimelineAction } from "@scroll/shared-types/timeline";
import { MIN_CLIP_DURATION } from "@scroll/shared-types/timeline";

interface ImageClipResizeHandlesProps {
  clip: TimelineClip;
  pixelsPerSecond: number;
  onDispatch: (action: TimelineAction) => void;
}

/**
 * Resize handles for image clips on the web timeline.
 *
 * Image clips have no source duration to clamp against (unlike video clips
 * which use `trimStart`/`trimEnd` against `sourceDuration`). Instead the
 * user-visible duration is `displayDuration`, dispatched via the shared
 * `RESIZE_IMAGE_CLIP` reducer action.
 *
 * Left handle: shifts `timelineStart` and shrinks/grows `displayDuration`
 * to keep the right edge fixed. Right handle: grows `displayDuration` only.
 * MIN_CLIP_DURATION is the only floor; pack-tight normalization in the
 * shared reducer handles cross-clip constraints on commit.
 */
export default function ImageClipResizeHandles({
  clip,
  pixelsPerSecond,
  onDispatch,
}: ImageClipResizeHandlesProps) {
  const draggingRef = useRef<"left" | "right" | null>(null);
  const startXRef = useRef(0);
  const startStateRef = useRef({ timelineStart: 0, displayDuration: 0 });
  const lastStateRef = useRef({ timelineStart: 0, displayDuration: 0 });

  const handleMouseDown = useCallback(
    (side: "left" | "right", e: React.MouseEvent) => {
      e.stopPropagation();
      draggingRef.current = side;
      startXRef.current = e.clientX;
      const timelineStart = clip.timelineStart ?? 0;
      const displayDuration = clip.displayDuration;
      startStateRef.current = { timelineStart, displayDuration };
      lastStateRef.current = { timelineStart, displayDuration };

      const handleMouseMove = (e: MouseEvent) => {
        const dx = e.clientX - startXRef.current;
        const dt = dx / pixelsPerSecond;
        const start = startStateRef.current;
        const originalEnd = start.timelineStart + start.displayDuration;

        let nextStart = start.timelineStart;
        let nextDuration = start.displayDuration;

        if (draggingRef.current === "left") {
          // Anchor the right edge; left edge shifts inward/outward.
          const candidateStart = start.timelineStart + dt;
          // Floor on duration (so the clip can't collapse to zero) and
          // floor on timelineStart (can't go negative).
          nextStart = Math.max(0, Math.min(candidateStart, originalEnd - MIN_CLIP_DURATION));
          nextDuration = originalEnd - nextStart;
        } else {
          // Anchor the left edge; right edge grows/shrinks.
          const candidateEnd = originalEnd + dt;
          const minEnd = start.timelineStart + MIN_CLIP_DURATION;
          nextStart = start.timelineStart;
          nextDuration = Math.max(MIN_CLIP_DURATION, candidateEnd - start.timelineStart);
          // Belt-and-suspenders: ensure right edge >= left + MIN.
          if (candidateEnd < minEnd) nextDuration = MIN_CLIP_DURATION;
        }

        lastStateRef.current = { timelineStart: nextStart, displayDuration: nextDuration };

        // Live dispatch — shared reducer will pack-tight via
        // normalizeExplicitClipStarts so neighbor positions adjust.
        onDispatch({
          type: "RESIZE_IMAGE_CLIP",
          clipId: clip.id,
          timelineStart: nextStart,
          displayDuration: nextDuration,
        });
      };

      const cleanup = () => {
        draggingRef.current = null;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("keydown", handleKeyDown);
      };

      const handleMouseUp = () => {
        cleanup();
      };

      // Escape cancels and reverts.
      const handleKeyDown = (ev: KeyboardEvent) => {
        if (ev.key !== "Escape") return;
        ev.preventDefault();
        onDispatch({
          type: "RESIZE_IMAGE_CLIP",
          clipId: clip.id,
          timelineStart: startStateRef.current.timelineStart,
          displayDuration: startStateRef.current.displayDuration,
        });
        lastStateRef.current = { ...startStateRef.current };
        cleanup();
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("keydown", handleKeyDown);
    },
    [clip.id, clip.timelineStart, clip.displayDuration, pixelsPerSecond, onDispatch],
  );

  return (
    <>
      <div
        className="timeline-trim-handle timeline-trim-handle-left"
        role="slider"
        aria-label="Resize image clip start"
        aria-valuemin={0}
        aria-valuenow={clip.timelineStart ?? 0}
        aria-valuetext={`Starts at ${(clip.timelineStart ?? 0).toFixed(2)} seconds`}
        onMouseDown={(e) => handleMouseDown("left", e)}
      >
        <div className="timeline-trim-handle-grip" />
      </div>
      <div
        className="timeline-trim-handle timeline-trim-handle-right"
        role="slider"
        aria-label="Resize image clip duration"
        aria-valuemin={MIN_CLIP_DURATION}
        aria-valuenow={clip.displayDuration}
        aria-valuetext={`Ends at ${((clip.timelineStart ?? 0) + clip.displayDuration).toFixed(2)} seconds`}
        onMouseDown={(e) => handleMouseDown("right", e)}
      >
        <div className="timeline-trim-handle-grip" />
      </div>
    </>
  );
}
